import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  getTeacherAccountLookup,
  requestPasswordReset,
  resendTeacherSetupLink
} from "../services/APIClient";
import { isPlaceholderWorkflowId } from "../utils/teacherWorkflowRequests";
import "./TeacherSendPasswordResetModal.css";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalisePrefill = (prefill) => ({
  teacherName: String(prefill?.teacherName || prefill?.teacher_name || "").trim(),
  email: String(prefill?.email || "").trim()
});

const firstPresentValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

const isFalseValue = (value) =>
  value === false || ["false", "0", "no"].includes(String(value || "").trim().toLowerCase());

const getRoleLabel = (teacher) => {
  const additionalRole = Array.isArray(teacher?.additionalRoles) ? teacher.additionalRoles[0] : "";
  return teacher?.roleLabel || teacher?.role || additionalRole || "Teacher";
};

const getAccountStatus = (teacher, fallbackStatus) => {
  const rawStatus = firstPresentValue(
    teacher?.accountStatus,
    teacher?.account_status,
    teacher?.userStatus,
    teacher?.user_status,
    teacher?.status,
    fallbackStatus
  );

  return rawStatus ? String(rawStatus).trim() : "Active";
};

const setupIncompleteStatuses = new Set([
  "pending",
  "invited",
  "invite sent",
  "invitation sent",
  "not set up",
  "not setup",
  "setup incomplete",
  "setup not complete",
  "awaiting setup"
]);

const resetUnavailableStatuses = new Set([
  "inactive",
  "disabled",
  "deleted",
  "removed"
]);

const needsSetupLink = (teacher) => {
  const status = normaliseText(teacher.accountStatus);

  return (
    setupIncompleteStatuses.has(status) ||
    isFalseValue(teacher.setupComplete) ||
    isFalseValue(teacher.hasCompletedSetup) ||
    isFalseValue(teacher.accountSetupComplete)
  );
};

const canReceiveResetLink = (teacher) =>
  Boolean(
    teacher?.email &&
    teacher?.active !== false &&
    (!Array.isArray(teacher?.availableActions) ||
      teacher.availableActions.length < 1 ||
      teacher.availableActions.includes("send_password_reset")) &&
    !needsSetupLink(teacher) &&
    !resetUnavailableStatuses.has(normaliseText(teacher.accountStatus))
  );

const normaliseTeacher = (teacher, fallbackStatus) => {
  const teacherData = teacher && typeof teacher === "object" ? teacher : {};
  const id =
    teacherData.pendingteacherid ||
    teacherData.pendingTeacherId ||
    teacherData.id ||
    teacherData._id ||
    teacherData.teacherid ||
    teacherData.teacherId ||
    teacherData.teacher_id ||
    teacherData.email;
  const name =
    teacherData.name ||
    teacherData.fullName ||
    teacherData.displayName ||
    [teacherData.firstName, teacherData.lastName].filter(Boolean).join(" ") ||
    "Unnamed teacher";
  const accountStatus = getAccountStatus(teacherData, fallbackStatus);

  return {
    ...teacherData,
    id,
    pendingteacherid: teacherData.pendingteacherid || teacherData.pendingTeacherId || teacherData.pending_teacher_id || id,
    name,
    email: teacherData.email || "",
    role: getRoleLabel(teacherData),
    accountStatus,
    availableActions: teacherData.availableActions || teacherData.available_actions || []
  };
};

const mergeTeachers = (teachers) => {
  const teachersByKey = new Map();

  teachers.forEach((teacher) => {
    const key = normaliseText(teacher.email || teacher.id);
    if (!key) {
      return;
    }

    const existingTeacher = teachersByKey.get(key);
    if (!existingTeacher || existingTeacher.accountStatus !== "Active") {
      teachersByKey.set(key, teacher);
    }
  });

  return Array.from(teachersByKey.values()).sort((firstTeacher, secondTeacher) =>
    firstTeacher.name.localeCompare(secondTeacher.name)
  );
};

const findSingleExactMatch = (teachers, prefillContext) => {
  const teacherName = normaliseText(prefillContext.teacherName);
  const email = normaliseText(prefillContext.email);

  if (!teacherName && !email) {
    return null;
  }

  const matches = teachers.filter((teacher) => {
    const nameMatches = teacherName && normaliseText(teacher.name) === teacherName;
    const emailMatches = email && normaliseText(teacher.email) === email;

    return nameMatches || emailMatches;
  });

  return matches.length === 1 ? matches[0] : null;
};

const getLookupRows = (lookupResponse) => {
  if (Array.isArray(lookupResponse)) return lookupResponse;
  if (Array.isArray(lookupResponse?.teachers)) return lookupResponse.teachers;
  if (Array.isArray(lookupResponse?.rows)) return lookupResponse.rows;
  if (Array.isArray(lookupResponse?.accounts)) return lookupResponse.accounts;
  return [];
};

function TeacherSendPasswordResetModal({
  show,
  handleClose,
  schoolid,
  currentTeacherId = null,
  prefill = null,
  onUpdateUserEmail = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [teachers, setTeachers] = useState([]);
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [sentTeacher, setSentTeacher] = useState(null);
  const [sentAction, setSentAction] = useState("reset");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  const filteredTeachers = useMemo(() => {
    const query = normaliseText(teacherSearchTerm);
    if (!query) return [];

    return teachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.role, teacher.accountStatus].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [teacherSearchTerm, teachers]);

  const selectedNeedsSetupLink = Boolean(selectedTeacher && needsSetupLink(selectedTeacher));
  const selectedCanReceiveReset = Boolean(selectedTeacher && canReceiveResetLink(selectedTeacher));
  const selectedCanContinue = Boolean(selectedTeacher?.email && !isSubmitting);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setTeachers([]);
    setTeacherSearchTerm(prefillContext.teacherName || prefillContext.email);
    setSelectedTeacher(null);
    setSentTeacher(null);
    setSentAction("reset");
    setError("");

    if (!schoolid) {
      setError("Teachers could not be loaded. Please try again.");
      return;
    }

    setIsLoading(true);
    getTeacherAccountLookup(schoolid)
      .then((lookupResponse) => {
        const normalisedTeachers = mergeTeachers(
          getLookupRows(lookupResponse).map((teacher) =>
            normaliseTeacher(teacher, teacher?.accountStatus || teacher?.account_status || "Unknown")
          )
        )
          .filter((teacher) => teacher.id && teacher.email)
          .filter((teacher) => teacher.active !== false)
          .filter((teacher) => !currentTeacherId || String(teacher.id) !== String(currentTeacherId));
        const prefilledTeacher = findSingleExactMatch(normalisedTeachers, prefillContext);

        setTeachers(normalisedTeachers);
        setSelectedTeacher(prefilledTeacher);
      })
      .catch((loadError) => {
        console.error("Error loading password reset teachers:", loadError);
        setError("Teachers could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [show, schoolid, currentTeacherId, prefillContext]);

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setError("");
  };

  const handleSendResetLink = async () => {
    if (!selectedCanContinue) {
      return;
    }

    if (selectedNeedsSetupLink) {
      setStage("setup");
      return;
    }

    if (!selectedCanReceiveReset) {
      setError("Choose an active teacher account first.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await requestPasswordReset(selectedTeacher.email);
      setSentTeacher(selectedTeacher);
      setSentAction("reset");
      setStage("success");
    } catch (submitError) {
      console.error("Error sending password reset link:", submitError);
      setError("The reset link could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendSetupLink = async () => {
    if (!selectedTeacher?.email || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const pendingTeacherId = selectedTeacher.pendingteacherid || selectedTeacher.id;

      if (isPlaceholderWorkflowId(pendingTeacherId)) {
        throw new Error("Missing real pending teacher id.");
      }

      await resendTeacherSetupLink(schoolid, pendingTeacherId);
      setSentTeacher(selectedTeacher);
      setSentAction("setup");
      setStage("success");
    } catch (submitError) {
      console.error("Error sending account setup link:", submitError);
      setError("The setup link could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherTeacher = () => {
    setStage("form");
    setTeacherSearchTerm("");
    setSelectedTeacher(null);
    setSentTeacher(null);
    setSentAction("reset");
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        message: sentAction === "setup" ? "Account setup link sent." : "Password reset link sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderStatusLabel = (teacher) => {
    if (needsSetupLink(teacher)) {
      return "Setup not complete";
    }

    if (canReceiveResetLink(teacher)) {
      return "Active account";
    }

    return teacher.accountStatus || "Could not check account";
  };

  const renderTeacherResults = () => {
    if (isLoading) {
      return (
        <div className="password-reset-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading teachers...</span>
        </div>
      );
    }

    if (!teacherSearchTerm.trim()) {
      return <p className="password-reset-muted">Start with the teacher's name or email.</p>;
    }

    if (filteredTeachers.length < 1) {
      return (
        <div className="password-reset-empty">
          <strong>No matching teachers found.</strong>
          <span>Try another teacher name or email.</span>
        </div>
      );
    }

    return filteredTeachers.map((teacher) => {
      const selected = selectedTeacher?.id === teacher.id;
      const setupRequired = needsSetupLink(teacher);

      return (
        <button
          key={teacher.id}
          type="button"
          className={`password-reset-result${selected ? " selected" : ""}${setupRequired ? " setup-required" : ""}`}
          onClick={() => handleTeacherSelect(teacher)}
          aria-label={`Select ${teacher.name}`}
          aria-pressed={selected}
        >
          <span className="password-reset-radio" aria-hidden="true" />
          <span className="password-reset-result-copy">
            <strong>{teacher.name}</strong>
            <span>{teacher.email}</span>
            <span className={`password-reset-pill${setupRequired ? " warning" : ""}`}>
              {renderStatusLabel(teacher)}
            </span>
          </span>
          {selected && <span className="password-reset-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderSelectedSummary = () => {
    if (!selectedTeacher) {
      return null;
    }

    return (
      <div className="password-reset-summary">
        <strong>Send to</strong>
        <span className="password-reset-row">
          <span>Teacher</span>
          <span>{selectedTeacher.name}</span>
        </span>
        <span className="password-reset-row">
          <span>Email</span>
          <span>{selectedTeacher.email}</span>
        </span>
        <span className="password-reset-row">
          <span>Status</span>
          <span>{renderStatusLabel(selectedTeacher)}</span>
        </span>
      </div>
    );
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Send Password Reset Link</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="password-reset-intro">Find the teacher who needs help signing in.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group controlId="teacherPasswordResetSearch">
          <Form.Label>Which teacher needs a reset link?</Form.Label>
          <Form.Text as="div" className="password-reset-field-label">
            Search teacher
          </Form.Text>
          <Form.Control
            type="search"
            value={teacherSearchTerm}
            onChange={(event) => {
              setTeacherSearchTerm(event.target.value);
              setSelectedTeacher(null);
              setError("");
            }}
            aria-label="Search teacher for password reset"
          />
          <Form.Text className="text-muted">
            Choose from teachers in this school.
          </Form.Text>
        </Form.Group>
        <div className="password-reset-results" aria-label="Matching teachers">
          {renderTeacherResults()}
        </div>
        {renderSelectedSummary()}
      </Modal.Body>
      <Modal.Footer>
        <span className="password-reset-footer-note">
          {selectedTeacher ? `${selectedTeacher.name} selected.` : "Choose the teacher first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSendResetLink}
          disabled={!selectedCanContinue}
        >
          {isSubmitting ? "Sending..." : "Send reset link"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSetupFallback = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Send Setup Link Instead</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="password-reset-intro">{selectedTeacher?.name} has not finished account setup.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="password-reset-warning">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            <strong>Password reset will not work yet.</strong>
            <span>Send the account setup link to {selectedTeacher?.email} instead.</span>
          </span>
        </Alert>
        {renderSelectedSummary()}
      </Modal.Body>
      <Modal.Footer>
        <span className="password-reset-footer-note">Setup link needed.</span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleSendSetupLink}
          disabled={!selectedTeacher?.email || isSubmitting}
        >
          {isSubmitting ? "Sending..." : "Send setup link"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => {
    const setupLinkSent = sentAction === "setup";

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">{setupLinkSent ? "Setup Link Sent" : "Reset Link Sent"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="password-reset-intro">{sentTeacher?.name} has been notified.</p>
          <Alert variant="success" className="password-reset-success">
            <FaCheckCircle aria-hidden="true" />
            <span>
              <strong>
                {setupLinkSent
                  ? `An account setup link has been sent to ${sentTeacher?.name}.`
                  : `A password reset link has been sent to ${sentTeacher?.name}.`}
              </strong>
              <span>The link was sent to {sentTeacher?.email}.</span>
            </span>
          </Alert>
          <div className="password-reset-next">
            <strong>What happens next</strong>
            <span>
              {setupLinkSent
                ? `${sentTeacher?.name} can finish account setup from the email.`
                : `${sentTeacher?.name} can reset their password from the email.`}
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <span className="password-reset-footer-note">
            {embedded ? "Done returns to SENSAI." : "Link sent."}
          </span>
          <Button variant="outline-secondary" onClick={resetForAnotherTeacher}>
            Send another reset
          </Button>
          {!setupLinkSent && onUpdateUserEmail && (
            <Button
              variant="outline-secondary"
              onClick={() => onUpdateUserEmail({
                teacherName: sentTeacher?.name,
                currentEmail: sentTeacher?.email
              })}
            >
              Update user email
            </Button>
          )}
          <Button variant="primary" onClick={handleDone}>
            Done
          </Button>
        </Modal.Footer>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-password-reset-modal"
    >
      {stage === "success" ? renderSuccess() : stage === "setup" ? renderSetupFallback() : renderForm()}
    </Modal>
  );
}

export default TeacherSendPasswordResetModal;
